import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useSiteContent } from '../hooks/useSiteContent';
import { useSEO } from '../hooks/useSEO';
import {
  Truck,
  Shield,
  Layers,
  TrendingUp,
  Building2,
  ArrowRight,
} from 'lucide-react';

/**
 * ZEP public landing page at "/".
 *
 * Pale-gold accent palette (replaces the former intense amber/yellow).
 * Primary solid: #c9a961  ·  Hover: #b8985a  ·  Light accent: #d4b976
 * Soft accent: #dec294  ·  Pale tint: rgba(201,169,97, …)
 */
export default function ZepLandingPage() {
  // Session 215.81 — moved off "/" (now serves Zion Underwriting
  // Partners). This page now lives at /platform-overview as an
  // internal reference and is noindex so it doesn't compete with
  // the customer home on GSC.
  useSEO({
    title: 'ZEP Platform Overview | Zion Underwriting Partners',
    description:
      'Internal overview of the Zion Enterprises Platform (ZEP) — quote-to-bind, endorsement, and accounting stack that powers Zion Underwriting Partners.',
    path: '/platform-overview',
    noindex: true,
  });

  // Session 213.78 — operator-editable homepage copy via CMS.
  const cms = useSiteContent('homepage', {
    hero_title: 'Leading Transportation Insurance Platforms',
    hero_subtitle: 'Purpose-built software for wholesalers, program administrators, and MGAs running transportation insurance programs. Built by operators — refined by real quote volume.',
    hero_cta_label: 'Get Started Now',
    hero_cta_href: 'mailto:platform@zionent.com?subject=Getting%20Started%20with%20ZEP',
    about_title: 'Why Zion Underwriting Partners',
    about_body: '',
  });

  // Split the hero title on the last word — that word renders in gold so the
  // emphasis matches the original design without the admin needing two fields.
  const heroTitleParts = (() => {
    const t = (cms.hero_title || '').trim();
    const idx = t.lastIndexOf(' ');
    if (idx === -1) return { lead: '', highlight: t };
    return { lead: t.slice(0, idx), highlight: t.slice(idx + 1) };
  })();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 overflow-x-hidden">
      {/* Ambient grid + glow background (pale gold) */}
      <div className="fixed inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(201,169,97,.35) 1px, transparent 1px), linear-gradient(90deg, rgba(201,169,97,.35) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
        <div className="absolute top-[-20%] right-[-10%] w-[50%] h-[50%] rounded-full blur-[180px] opacity-[0.12]" style={{ backgroundColor: '#c9a961' }} />
        <div className="absolute bottom-[-20%] left-[-10%] w-[50%] h-[50%] bg-sky-500 rounded-full blur-[180px] opacity-[0.07]" />
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-slate-800/60 backdrop-blur-sm bg-slate-950/60 sticky top-0">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/zion_enterprises_logo.png" alt="Zion Enterprises LLC" className="h-10 w-auto" />
            <div>
              <p className="font-bold text-slate-100 tracking-tight">Zion Enterprises</p>
              <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: '#d4b976' }}>Platform</p>
            </div>
          </div>
          {/* Subtle staff sign-in — not a primary CTA */}
          <Link
            to="/platform"
            className="text-xs font-medium tracking-wider uppercase text-slate-400 hover:text-[#d4b976] border-b border-transparent hover:border-[#d4b976]/50 pb-0.5 transition-colors"
            data-testid="zep-landing-platform-login-btn"
          >
            Staff Sign In
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-24 pb-32">
        <div className="max-w-3xl">
          <div
            className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold tracking-wider uppercase mb-6 border"
            style={{ backgroundColor: 'rgba(201,169,97,0.08)', borderColor: 'rgba(201,169,97,0.25)', color: '#dec294' }}
          >
            <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ backgroundColor: '#d4b976' }} />
            Zion Enterprises Platform · ZEP
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05] mb-6" data-testid="zep-hero-title">
            {heroTitleParts.lead}{heroTitleParts.lead ? ' ' : ''}<span style={{ color: '#d4b976' }}>{heroTitleParts.highlight}</span>
          </h1>
          <p className="text-lg text-slate-300 leading-relaxed mb-10 max-w-2xl" data-testid="zep-hero-subtitle">
            {cms.hero_subtitle}
          </p>
          <div className="flex flex-wrap gap-4">
            <a href={cms.hero_cta_href}>
              <Button
                size="lg"
                className="font-semibold px-8 h-12 border-0"
                style={{ backgroundColor: '#c9a961', color: '#0f172a' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#b8985a'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#c9a961'; }}
                data-testid="zep-landing-cta-primary"
              >
                {cms.hero_cta_label}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </a>
            <a href="mailto:platform@zionent.com?subject=ZEP%20Demo%20Request">
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-900 px-8 h-12"
                data-testid="zep-landing-cta-contact"
              >
                Request a Demo
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* What we do — 3 pillars */}
      <section className="relative z-10 border-y border-slate-800/60 bg-slate-900/30 backdrop-blur-sm py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.3em] mb-3" style={{ color: '#d4b976' }}>What we build</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Infrastructure for the whole transportation insurance lifecycle.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <Pillar
              icon={Truck}
              title="Dynamic Rating Engines"
              body="State-, vehicle-, and operation-aware rating logic with carrier shopping and auto-quote evaluation. Configured from a UI, not code."
            />
            <Pillar
              icon={Layers}
              title="Multi-Coverage Quote Workflow"
              body="APD, MTC, TI, NTL, and TGL handled in one unified submission. Binding, endorsements, cancellations, and renewals all covered."
            />
            <Pillar
              icon={TrendingUp}
              title="Program Operations"
              body="Claims with automated acknowledgment workflows, loss runs, agency accounting, Stripe payments, and audit-grade document trails."
            />
          </div>
        </div>
      </section>

      {/* For whom */}
      <section className="relative z-10 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.3em] mb-3" style={{ color: '#d4b976' }}>Who it's for</p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-6">
                Programs that refuse to compromise on speed, compliance, or control.
              </h2>
              <p className="text-slate-300 leading-relaxed mb-6">
                Each tenant on ZEP runs a fully white-labeled workspace — their own domain, branding,
                carrier lineup, rating tables, and agency network. Your operation, your brand, our
                platform doing the heavy lifting.
              </p>
              <ul className="space-y-3 text-slate-200">
                {[
                  'Wholesale transportation insurance MGAs',
                  'Program administrators with multi-carrier shopping',
                  'Operators scaling from one state to nationwide',
                ].map((t) => (
                  <li key={t} className="flex items-center gap-3">
                    <div className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: '#d4b976' }} />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 shadow-2xl p-8 relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle, rgba(201,169,97,.45) 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                <div className="relative">
                  <div className="flex items-center gap-2 mb-6">
                    <Shield className="h-5 w-5" style={{ color: '#d4b976' }} />
                    <span className="text-xs font-mono text-slate-400 uppercase tracking-wider">ZEP Tenant Workspace</span>
                  </div>
                  <div className="space-y-3">
                    <StatRow label="Active Policies" value="8,421" />
                    <StatRow label="Quote Volume (30d)" value="$2.4M" />
                    <StatRow label="Avg Time to Bind" value="11 min" accent />
                    <StatRow label="Carrier Shopping Hit Rate" value="94%" />
                    <StatRow label="Auto-Approval Rate" value="67%" />
                  </div>
                  <div className="mt-6 pt-4 border-t border-slate-700/70 flex items-center gap-2 text-xs text-slate-400">
                    <Building2 className="h-3.5 w-3.5" />
                    Sample workspace metrics
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="relative z-10 border-t border-slate-800/60 bg-slate-900/40 py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            Ready to run a transportation insurance program the right way?
          </h2>
          <p className="text-slate-300 mb-8">
            ZEP is licensed to a limited number of partners each year. Reach out to discuss whether
            your program is a fit.
          </p>
          <a href="mailto:platform@zionent.com">
            <Button
              size="lg"
              className="font-semibold px-10 h-12 border-0"
              style={{ backgroundColor: '#c9a961', color: '#0f172a' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#b8985a'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#c9a961'; }}
            >
              Contact Zion Enterprises
            </Button>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-slate-800/60 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} Zion Enterprises LLC · All rights reserved</div>
          <div className="flex items-center gap-5">
            <span>platform@zionent.com</span>
            <Link to="/platform" className="hover:text-[#d4b976] transition-colors">Staff Sign In</Link>
            <Link to="/login" className="hover:text-[#d4b976] transition-colors">Tenant Sign In</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Pillar({ icon: Icon, title, body }) {
  return (
    <div
      className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-xl p-6 transition-colors"
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(201,169,97,0.45)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = ''; }}
    >
      <div
        className="h-10 w-10 rounded-lg flex items-center justify-center mb-4 border"
        style={{ backgroundColor: 'rgba(201,169,97,0.12)', borderColor: 'rgba(201,169,97,0.3)' }}
      >
        <Icon className="h-5 w-5" style={{ color: '#d4b976' }} />
      </div>
      <h3 className="font-bold text-lg mb-2">{title}</h3>
      <p className="text-sm text-slate-400 leading-relaxed">{body}</p>
    </div>
  );
}

function StatRow({ label, value, accent = false }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-800/70 last:border-0">
      <span className="text-xs text-slate-400">{label}</span>
      <span className="text-lg font-bold font-mono" style={accent ? { color: '#d4b976' } : { color: '#f1f5f9' }}>
        {value}
      </span>
    </div>
  );
}
